import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Crown, Zap, CheckCircle2 } from 'lucide-react';

interface ConversionStats {
  inputLength: number;
  outputLength: number;
  processingTime: number;
  operationsCount: number;
}

interface RomanResult {
  roman: string;
  decimal: string;
  breakdown: string[];
  validation: {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  };
  stats: Omit<ConversionStats, 'operationsCount'>;
}

type InputMode = 'decimal' | 'roman';
type RomanStyle = 'classical' | 'medieval' | 'modern';
type CaseStyle = 'upper' | 'lower' | 'title';

const RomanNumeralConverterTool: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [inputMode, setInputMode] = useState<InputMode>('decimal');
  const [romanStyle, setRomanStyle] = useState<RomanStyle>('classical');
  const [caseStyle, setCaseStyle] = useState<CaseStyle>('upper');
  const [results, setResults] = useState<RomanResult>({
    roman: '',
    decimal: '',
    breakdown: [],
    validation: {
      isValid: true,
      errors: [],
      warnings: []
    },
    stats: {
      inputLength: 0,
      outputLength: 0,
      processingTime: 0
    }
  });
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [showBreakdown, setShowBreakdown] = useState<boolean>(true);
  const [stats, setStats] = useState<ConversionStats>({
    inputLength: 0,
    outputLength: 0,
    processingTime: 0,
    operationsCount: 0
  });
  const [notification, setNotification] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Roman numeral mappings
  const romanMappings = {
    classical: [
      { value: 1000, numeral: 'M' },
      { value: 900, numeral: 'CM' },
      { value: 500, numeral: 'D' },
      { value: 400, numeral: 'CD' },
      { value: 100, numeral: 'C' },
      { value: 90, numeral: 'XC' },
      { value: 50, numeral: 'L' },
      { value: 40, numeral: 'XL' },
      { value: 10, numeral: 'X' },
      { value: 9, numeral: 'IX' },
      { value: 5, numeral: 'V' },
      { value: 4, numeral: 'IV' },
      { value: 1, numeral: 'I' }
    ],
    medieval: [
      { value: 1000, numeral: 'M' },
      { value: 500, numeral: 'D' },
      { value: 100, numeral: 'C' },
      { value: 50, numeral: 'L' },
      { value: 10, numeral: 'X' },
      { value: 5, numeral: 'V' },
      { value: 1, numeral: 'I' }
    ],
    modern: [
      { value: 1000000, numeral: 'M̄' },
      { value: 900000, numeral: 'C̄M̄' },
      { value: 500000, numeral: 'D̄' },
      { value: 400000, numeral: 'C̄D̄' },
      { value: 100000, numeral: 'C̄' },
      { value: 90000, numeral: 'X̄C̄' },
      { value: 50000, numeral: 'L̄' },
      { value: 40000, numeral: 'X̄L̄' },
      { value: 10000, numeral: 'X̄' },
      { value: 9000, numeral: 'MX̄' },
      { value: 5000, numeral: 'V̄' },
      { value: 4000, numeral: 'MV̄' },
      { value: 1000, numeral: 'M' },
      { value: 900, numeral: 'CM' },
      { value: 500, numeral: 'D' },
      { value: 400, numeral: 'CD' },
      { value: 100, numeral: 'C' },
      { value: 90, numeral: 'XC' },
      { value: 50, numeral: 'L' },
      { value: 40, numeral: 'XL' },
      { value: 10, numeral: 'X' },
      { value: 9, numeral: 'IX' },
      { value: 5, numeral: 'V' },
      { value: 4, numeral: 'IV' },
      { value: 1, numeral: 'I' }
    ]
  };

  const formatCase = useCallback((roman: string): string => {
    switch (caseStyle) {
      case 'upper':
        return roman.toUpperCase();
      case 'lower':
        return roman.toLowerCase();
      case 'title':
        return roman.charAt(0).toUpperCase() + roman.slice(1).toLowerCase();
      default:
        return roman;
    }
  }, [caseStyle]);

  const decimalToRoman = useCallback((num: number): { roman: string; breakdown: string[] } => {
    if (num <= 0) {
      throw new Error('Roman numerals do not support zero or negative numbers');
    }
    
    const maxValue = romanStyle === 'modern' ? 3999999 : 3999;
    if (num > maxValue) {
      throw new Error(`Number too large for ${romanStyle} style (max: ${maxValue})`);
    }

    let result = '';
    let remaining = num;
    const breakdown: string[] = [];
    const mappings = romanMappings[romanStyle];

    for (const { value, numeral } of mappings) {
      while (remaining >= value) {
        result += numeral;
        breakdown.push(`${remaining} - ${value} = ${remaining - value} (${numeral})`);
        remaining -= value;
      }
    }

    return { roman: formatCase(result), breakdown };
  }, [romanStyle, formatCase]);

  const romanToDecimal = useCallback((roman: string): { decimal: number; breakdown: string[]; validation: RomanResult['validation'] } => {
    const cleanRoman = roman.toUpperCase().trim();
    if (!cleanRoman) {
      throw new Error('Empty roman numeral');
    }

    const validation: RomanResult['validation'] = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Validate characters
    const validChars = romanStyle === 'modern' ? /^[IVXLCDM̄]+$/ : /^[IVXLCDM]+$/;
    if (!validChars.test(cleanRoman)) {
      validation.isValid = false;
      validation.errors.push('Contains invalid characters for Roman numerals');
    }

    // Check for invalid patterns
    const invalidPatterns = [
      /IIII/, /VV/, /XXXX/, /LL/, /CCCC/, /DD/, /MMMM/
    ];
    
    for (const pattern of invalidPatterns) {
      if (pattern.test(cleanRoman)) {
        validation.warnings.push('Contains non-standard repetitive patterns');
        break;
      }
    }

    // Check subtractive notation rules
    const invalidSubtractive = [
      /IL/, /IC/, /ID/, /IM/, // I can only subtract from V and X
      /VX/, /VL/, /VC/, /VD/, /VM/, // V cannot be used in subtractive notation
      /XD/, /XM/, // X can only subtract from L and C
      /LC/, /LD/, /LM/, // L cannot be used in subtractive notation
      /DM/ // D cannot be used in subtractive notation
    ];

    for (const pattern of invalidSubtractive) {
      if (pattern.test(cleanRoman)) {
        validation.warnings.push('Contains invalid subtractive notation');
        break;
      }
    }

    let result = 0;
    let i = 0;
    const breakdown: string[] = [];
    const mappings = romanMappings[romanStyle];

    for (const { value, numeral } of mappings) {
      while (cleanRoman.substr(i, numeral.length) === numeral) {
        result += value;
        breakdown.push(`${numeral} = ${value} (total: ${result})`);
        i += numeral.length;
      }
    }

    if (i < cleanRoman.length) {
      validation.isValid = false;
      validation.errors.push(`Unrecognized sequence: ${cleanRoman.substr(i)}`);
    }

    // Verify the conversion by converting back
    if (validation.isValid && result > 0) {
      try {
        const { roman: convertedBack } = decimalToRoman(result);
        if (convertedBack.toUpperCase() !== cleanRoman) {
          validation.warnings.push('Non-standard form detected (valid but not optimal)');
        }
      } catch (e) {
        // Ignore errors in reverse conversion for validation
      }
    }

    return { decimal: result, breakdown, validation };
  }, [romanStyle, decimalToRoman]);

  const convertNumber = useCallback((): RomanResult => {
    const startTime = performance.now();
    
    if (!inputText.trim()) {
      return {
        roman: '',
        decimal: '',
        breakdown: [],
        validation: {
          isValid: true,
          errors: [],
          warnings: []
        },
        stats: {
          inputLength: 0,
          outputLength: 0,
          processingTime: 0
        }
      };
    }

    try {
      let roman = '';
      let decimal = '';
      let breakdown: string[] = [];
      let validation: RomanResult['validation'] = {
        isValid: true,
        errors: [],
        warnings: []
      };

      if (inputMode === 'decimal') {
        const num = parseInt(inputText.trim());
        if (isNaN(num)) {
          throw new Error('Invalid decimal number');
        }
        
        const result = decimalToRoman(num);
        roman = result.roman;
        decimal = num.toString();
        breakdown = result.breakdown;
      } else {
        const result = romanToDecimal(inputText);
        roman = formatCase(inputText.trim());
        decimal = result.decimal.toString();
        breakdown = result.breakdown;
        validation = result.validation;
      }

      const endTime = performance.now();
      
      setErrorMessage('');
      
      return {
        roman,
        decimal,
        breakdown,
        validation,
        stats: {
          inputLength: inputText.length,
          outputLength: roman.length + decimal.length,
          processingTime: endTime - startTime
        }
      };
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Conversion error');
      return {
        roman: '',
        decimal: '',
        breakdown: [],
        validation: {
          isValid: false,
          errors: [error instanceof Error ? error.message : 'Conversion error'],
          warnings: []
        },
        stats: {
          inputLength: inputText.length,
          outputLength: 0,
          processingTime: performance.now() - startTime
        }
      };
    }
  }, [inputText, inputMode, decimalToRoman, romanToDecimal, formatCase]);

  useEffect(() => {
    const result = convertNumber();
    setResults(result);
    setStats(prev => ({
      ...result.stats,
      operationsCount: prev.operationsCount + (inputText.trim() ? 1 : 0)
    }));
  }, [convertNumber]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (text: string, label: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification(`${label} copied to clipboard!`);
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = (): void => {
    const content = `Roman Numeral Conversion Results
Input: ${inputText} (${inputMode.toUpperCase()})
Style: ${romanStyle} | Case: ${caseStyle}

Roman Numeral: ${results.roman}
Decimal Number: ${results.decimal}

Conversion Breakdown:
${results.breakdown.join('\n')}

Validation:
Valid: ${results.validation.isValid ? 'Yes' : 'No'}
${results.validation.errors.length > 0 ? `Errors: ${results.validation.errors.join(', ')}` : ''}
${results.validation.warnings.length > 0 ? `Warnings: ${results.validation.warnings.join(', ')}` : ''}

Conversion completed in ${results.stats.processingTime.toFixed(2)}ms
`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'roman-conversion.txt';
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          // Extract first number or roman numeral from file
          const numberMatch = result.match(/\d+/);
          const romanMatch = result.match(/[IVXLCDM̄]+/i);
          
          if (numberMatch) {
            setInputText(numberMatch[0]);
            setInputMode('decimal');
            showNotification('Number loaded from file!');
          } else if (romanMatch) {
            setInputText(romanMatch[0]);
            setInputMode('roman');
            showNotification('Roman numeral loaded from file!');
          } else {
            showNotification('No valid number or roman numeral found in file');
          }
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = (): void => {
    setInputText('');
    setResults({
      roman: '',
      decimal: '',
      breakdown: [],
      validation: {
        isValid: true,
        errors: [],
        warnings: []
      },
      stats: {
        inputLength: 0,
        outputLength: 0,
        processingTime: 0
      }
    });
    setStats({
      inputLength: 0,
      outputLength: 0,
      processingTime: 0,
      operationsCount: 0
    });
    setErrorMessage('');
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSampleNumbers = (): void => {
    const samples = ['1994', '2024', '3999', '444', '1776'];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
    setInputMode('decimal');
  };

  const loadSampleRoman = (): void => {
    const samples = ['MCMXC', 'MMXXIV', 'CDXLIV', 'DCCCLXXXVIII', 'MCDXC'];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
    setInputMode('roman');
  };

  const generateRandomNumber = (): void => {
    const max = romanStyle === 'modern' ? 99999 : 3999;
    const randomNum = Math.floor(Math.random() * max) + 1;
    setInputText(randomNum.toString());
    setInputMode('decimal');
  };

  const swapMode = (): void => {
    if (results.roman && results.decimal) {
      if (inputMode === 'decimal') {
        setInputText(results.roman);
        setInputMode('roman');
      } else {
        setInputText(results.decimal);
        setInputMode('decimal');
      }
    }
  };

  const validateRoman = (): void => {
    if (!results.roman && !inputText) {
      showNotification('No roman numeral to validate!');
      return;
    }
    
    const { validation } = results;
    if (validation.isValid) {
      showNotification('✅ Valid Roman numeral!');
    } else {
      showNotification(`❌ Invalid: ${validation.errors.join(', ')}`);
    }
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-amber-900 to-red-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-amber-500 to-red-600 rounded-lg">
              <Crown className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Roman Numeral Converter</h1>
              <p className="text-gray-300 text-sm">Convert between Roman numerals and decimal numbers with validation</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Input Section */}
        <div className="mb-6 bg-gray-800 rounded-lg p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Input */}
            <div>
              <label className="block text-white font-medium mb-2">
                Input {inputMode === 'decimal' ? 'Number' : 'Roman Numeral'}
              </label>
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder={inputMode === 'decimal' ? 'Enter a number (1-3999)...' : 'Enter a Roman numeral...'}
                className="w-full bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none font-mono text-lg"
              />
              {errorMessage && (
                <div className="mt-1 text-red-400 text-sm">
                  {errorMessage}
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="space-y-4">
              {/* Input Mode */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Input Type
                </label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setInputMode('decimal')}
                    className={`flex-1 px-4 py-3 rounded-lg transition-all ${
                      inputMode === 'decimal'
                        ? 'bg-amber-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Decimal
                  </button>
                  <button
                    onClick={() => setInputMode('roman')}
                    className={`flex-1 px-4 py-3 rounded-lg transition-all ${
                      inputMode === 'roman'
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Roman
                  </button>
                </div>
              </div>

              {/* Style Selection */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Roman Style
                </label>
                <select
                  value={romanStyle}
                  onChange={(e) => setRomanStyle(e.target.value as RomanStyle)}
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                >
                  <option value="classical">Classical (1-3999)</option>
                  <option value="medieval">Medieval (additive only)</option>
                  <option value="modern">Modern (1-3,999,999)</option>
                </select>
              </div>

              {/* Case Style */}
              <div>
                <label className="block text-white font-medium mb-2">
                  Case Style
                </label>
                <select
                  value={caseStyle}
                  onChange={(e) => setCaseStyle(e.target.value as CaseStyle)}
                  className="w-full bg-gray-900 text-white p-3 rounded-lg border border-gray-600 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                >
                  <option value="upper">UPPERCASE</option>
                  <option value="lower">lowercase</option>
                  <option value="title">Title Case</option>
                </select>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="mt-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={showBreakdown}
                onChange={(e) => setShowBreakdown(e.target.checked)}
                className="w-4 h-4 text-amber-600 bg-gray-700 border-gray-600 rounded focus:ring-amber-500"
              />
              <span className="text-white">Show conversion breakdown</span>
            </label>
          </div>
        </div>

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Conversion Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-amber-400">{stats.inputLength}</div>
                <div className="text-gray-300 text-sm">Input Length</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-red-400">{stats.outputLength}</div>
                <div className="text-gray-300 text-sm">Output Length</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Conversions</div>
              </div>
            </div>
            
            {/* Roman Numeral Info */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <h4 className="text-white font-medium mb-2">Roman Numeral System</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                <div className="text-gray-300">
                  <strong>Basic:</strong> I(1), V(5), X(10), L(50), C(100), D(500), M(1000)
                </div>
                <div className="text-gray-300">
                  <strong>Subtractive:</strong> IV(4), IX(9), XL(40), XC(90), CD(400), CM(900)
                </div>
                <div className="text-gray-300">
                  <strong>Rules:</strong> Left-to-right largest to smallest, subtract when smaller precedes larger
                </div>
                <div className="text-gray-300">
                  <strong>Classical Range:</strong> 1 to 3,999
                </div>
                <div className="text-gray-300">
                  <strong>Modern Extensions:</strong> Vinculum (overline) for thousands
                </div>
                <div className="text-gray-300">
                  <strong>Validation:</strong> Checks standard form and rules
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        <div className="flex-1 mb-6">
          <h2 className="text-xl font-bold text-white mb-4">Conversion Results</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Roman Numeral Result */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Roman Numeral</h3>
                <button
                  onClick={() => copyToClipboard(results.roman, 'Roman Numeral')}
                  disabled={!results.roman}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-4 rounded border min-h-[100px]">
                {results.roman ? (
                  <div>
                    <div className="text-3xl font-bold text-amber-400 font-serif mb-2">
                      {results.roman}
                    </div>
                    <div className="text-gray-400 text-sm">
                      Style: {romanStyle} | Case: {caseStyle}
                    </div>
                    {/* Validation Status */}
                    <div className="mt-2">
                      {results.validation.isValid ? (
                        <span className="text-green-400 text-sm">✅ Valid</span>
                      ) : (
                        <span className="text-red-400 text-sm">❌ Invalid</span>
                      )}
                      {results.validation.warnings.length > 0 && (
                        <span className="text-yellow-400 text-sm ml-2">⚠️ Warnings</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500">Enter a number to see Roman conversion</div>
                )}
              </div>
            </div>

            {/* Decimal Result */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">Decimal Number</h3>
                <button
                  onClick={() => copyToClipboard(results.decimal, 'Decimal Number')}
                  disabled={!results.decimal}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-900 p-4 rounded border min-h-[100px]">
                {results.decimal ? (
                  <div>
                    <div className="text-3xl font-bold text-red-400 mb-2">
                      {parseInt(results.decimal).toLocaleString()}
                    </div>
                    <div className="text-gray-400 text-sm">
                      Raw value: {results.decimal}
                    </div>
                  </div>
                ) : (
                  <div className="text-gray-500">Enter a Roman numeral to see decimal conversion</div>
                )}
              </div>
            </div>
          </div>

          {/* Validation Messages */}
          {(results.validation.errors.length > 0 || results.validation.warnings.length > 0) && (
            <div className="mt-4 bg-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-3">Validation Results</h3>
              
              {results.validation.errors.length > 0 && (
                <div className="mb-3">
                  <h4 className="text-red-400 font-medium mb-1">Errors:</h4>
                  <ul className="text-red-300 text-sm space-y-1">
                    {results.validation.errors.map((error, index) => (
                      <li key={index}>• {error}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              {results.validation.warnings.length > 0 && (
                <div>
                  <h4 className="text-yellow-400 font-medium mb-1">Warnings:</h4>
                  <ul className="text-yellow-300 text-sm space-y-1">
                    {results.validation.warnings.map((warning, index) => (
                      <li key={index}>• {warning}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Breakdown */}
          {showBreakdown && results.breakdown.length > 0 && (
            <div className="mt-4 bg-gray-800 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-white mb-3">Conversion Breakdown</h3>
              <div className="bg-gray-900 p-3 rounded border max-h-60 overflow-y-auto">
                <div className="space-y-1 font-mono text-sm">
                  {results.breakdown.map((step, index) => (
                    <div key={index} className="text-gray-300">
                      {step}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSampleNumbers}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Number
            </button>
            <button
              onClick={loadSampleRoman}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Roman
            </button>
            <button
              onClick={generateRandomNumber}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
            >
              Random Number
            </button>
            <button
              onClick={swapMode}
              disabled={!results.roman || !results.decimal}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
            >
              Swap Input/Output
            </button>
            <button
              onClick={validateRoman}
              disabled={!results.roman && !inputText}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
            >
              Validate Roman
            </button>
            <label className="cursor-pointer">
              <input
                type="file"
                accept=".txt,.csv,.json"
                onChange={handleFileUpload}
                className="hidden"
              />
              <div className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors text-sm flex items-center space-x-2">
                <Upload className="w-4 h-4" />
                <span>Upload File</span>
              </div>
            </label>
            <button
              onClick={downloadFile}
              disabled={!results.roman && !results.decimal}
              className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Download Results</span>
            </button>
            <button
              onClick={clearAll}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Roman Numeral Reference */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Roman Numeral Reference</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 text-center">
            {[
              { roman: 'I', value: 1 },
              { roman: 'V', value: 5 },
              { roman: 'X', value: 10 },
              { roman: 'L', value: 50 },
              { roman: 'C', value: 100 },
              { roman: 'D', value: 500 },
              { roman: 'M', value: 1000 }
            ].map(({ roman, value }) => (
              <div key={roman} className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-amber-400 font-serif">{roman}</div>
                <div className="text-gray-300 text-sm">{value}</div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 text-center">
            <div className="bg-gray-700 p-2 rounded">
              <div className="text-lg font-bold text-red-400">IV</div>
              <div className="text-gray-300 text-xs">4</div>
            </div>
            <div className="bg-gray-700 p-2 rounded">
              <div className="text-lg font-bold text-red-400">IX</div>
              <div className="text-gray-300 text-xs">9</div>
            </div>
            <div className="bg-gray-700 p-2 rounded">
              <div className="text-lg font-bold text-red-400">XL</div>
              <div className="text-gray-300 text-xs">40</div>
            </div>
            <div className="bg-gray-700 p-2 rounded">
              <div className="text-lg font-bold text-red-400">XC</div>
              <div className="text-gray-300 text-xs">90</div>
            </div>
            <div className="bg-gray-700 p-2 rounded">
              <div className="text-lg font-bold text-red-400">CD</div>
              <div className="text-gray-300 text-xs">400</div>
            </div>
            <div className="bg-gray-700 p-2 rounded">
              <div className="text-lg font-bold text-red-400">CM</div>
              <div className="text-gray-300 text-xs">900</div>
            </div>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default RomanNumeralConverterTool;